import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  ApiError,
  type RoleAssignment,
  type RoleId,
  type RoleListResponse,
  type UserLookupResponse,
  type UserSearchResponse,
} from '../api/client';
import { useCurrentUser } from '../api/useCurrentUser';
import './AdminUsers.css';
import { formatDateTime } from '../api/formatDate';

/**
 * /admin/users — role management surface.
 *
 * Super-admin only (gated server-side via the role check on the API
 * endpoints; mirrored client-side via useCurrentUser for UX). Lists the
 * current explicit role assignments and lets a super-admin add, change, or
 * revoke them. Users without an explicit row default to 'kaimahi'.
 *
 * Phase 1b minimal: paste OID + pick role. Microsoft Graph integration for
 * email → OID lookup is a follow-up (see #64 description).
 */

const ROLES: readonly RoleId[] = [
  'kaimahi',
  'manager',
  'exec-manager',
  'administrator',
  'super-admin',
];

export function AdminUsers() {
  const { t } = useTranslation();
  const callApi = useApi();
  const currentUser = useCurrentUser();

  const [listState, setListState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; rows: RoleAssignment[] }
    | { kind: 'forbidden' }
    | { kind: 'error'; message: string; status?: number }
  >({ kind: 'loading' });

  const [actionState, setActionState] = useState<
    | { kind: 'idle' }
    | { kind: 'working'; oid: string }
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  const loadList = useCallback(async () => {
    setListState({ kind: 'loading' });
    try {
      const result = await callApi<RoleListResponse>('/admin/roles');
      setListState({ kind: 'ok', rows: result.rows });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setListState({ kind: 'forbidden' });
        } else {
          setListState({
            kind: 'error',
            status: err.status,
            message: err.message,
          });
        }
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setListState({ kind: 'error', message });
      }
    }
    // callApi is a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Hydrate display_name for rows missing it (legacy rows from before the
  // display_name column was added). Concurrent lookups, best-effort —
  // failures leave the row unresolved which renders as "Unknown user".
  useEffect(() => {
    if (listState.kind !== 'ok') return;
    const toResolve = listState.rows.filter((r) => !r.display_name);
    if (toResolve.length === 0) return;
    let cancelled = false;
    void (async () => {
      const resolved = await Promise.allSettled(
        toResolve.map((row) =>
          callApi<UserLookupResponse>(
            `/admin/users/lookup?oid=${encodeURIComponent(row.kaimahi_oid)}`,
          ).then((res) => ({ oid: row.kaimahi_oid, name: res.display_name })),
        ),
      );
      if (cancelled) return;
      const updates = new Map<string, string>();
      for (const r of resolved) {
        if (r.status === 'fulfilled' && r.value.name) {
          updates.set(r.value.oid, r.value.name);
        }
      }
      if (updates.size === 0) return;
      setListState((prev) => {
        if (prev.kind !== 'ok') return prev;
        return {
          kind: 'ok',
          rows: prev.rows.map((row) =>
            updates.has(row.kaimahi_oid)
              ? { ...row, display_name: updates.get(row.kaimahi_oid) ?? null }
              : row,
          ),
        };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [listState, callApi]);

  const onAssign = useCallback(
    async (
      oid: string,
      role_id: RoleId,
      notes: string | null,
      display_name: string | null = null,
    ) => {
      setActionState({ kind: 'working', oid });
      try {
        await callApi('/admin/roles', {
          method: 'POST',
          body: JSON.stringify({
            kaimahi_oid: oid,
            role_id,
            assignment_notes: notes,
            display_name,
          }),
          headers: { 'content-type': 'application/json' },
        });
        setActionState({
          kind: 'success',
          message: t('admin.assignSuccess', { role: role_id }),
        });
        await loadList();
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed';
        setActionState({ kind: 'error', message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadList, t],
  );

  const onResetPin = useCallback(
    async (oid: string) => {
      if (!window.confirm(t('admin.resetPinConfirm'))) {
        return;
      }
      setActionState({ kind: 'working', oid });
      try {
        await callApi(`/admin/users/${oid}/share-pin`, { method: 'DELETE' });
        setActionState({
          kind: 'success',
          message: t('admin.resetPinSuccess'),
        });
        await loadList();
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed';
        setActionState({ kind: 'error', message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadList, t],
  );

  const onRevoke = useCallback(
    async (oid: string) => {
      if (
        !window.confirm(t('admin.revokeConfirm'))
      ) {
        return;
      }
      setActionState({ kind: 'working', oid });
      try {
        await callApi(`/admin/roles/${oid}`, { method: 'DELETE' });
        setActionState({
          kind: 'success',
          message: t('admin.revokeSuccess'),
        });
        await loadList();
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed';
        setActionState({ kind: 'error', message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadList, t],
  );

  // Client-side gate. The API enforces super-admin too; this is for UX only.
  if (currentUser.kind === 'ok' && currentUser.data.role.roleId !== 'super-admin') {
    return (
      <article className="admin-users" aria-labelledby="admin-title">
        <p className="admin-users__status admin-users__status--error">
          {t('admin.forbidden')}
        </p>
      </article>
    );
  }

  return (
    <article className="admin-users" aria-labelledby="admin-title">
      <header className="admin-users__header">
        <h2 id="admin-title" className="admin-users__title">
          {t('admin.title')}
        </h2>
        <p className="admin-users__subtitle">{t('admin.subtitle')}</p>
      </header>

      {actionState.kind === 'success' && (
        <p className="admin-users__toast admin-users__toast--ok">
          {actionState.message}
        </p>
      )}
      {actionState.kind === 'error' && (
        <p className="admin-users__toast admin-users__toast--error">
          {actionState.message}
        </p>
      )}

      <AssignForm
        currentUserOid={
          currentUser.kind === 'ok' ? currentUser.data.user.oid : ''
        }
        disabled={actionState.kind === 'working'}
        onSubmit={onAssign}
      />

      <section className="admin-users__section">
        <h3 className="admin-users__section-title">
          {t('admin.explicitAssignments')}{' '}
          {listState.kind === 'ok' && (
            <span className="admin-users__section-count">
              {t('admin.assignmentCount', { count: listState.rows.length })}
            </span>
          )}
        </h3>

        {listState.kind === 'loading' && (
          <p className="admin-users__status">{t('admin.loading')}</p>
        )}

        {listState.kind === 'forbidden' && (
          <p className="admin-users__status admin-users__status--error">
            {t('admin.forbidden')}
          </p>
        )}

        {listState.kind === 'error' && (
          <p className="admin-users__status admin-users__status--error">
            {listState.status
              ? `${String(listState.status)} — ${listState.message}`
              : listState.message}
          </p>
        )}

        {listState.kind === 'ok' && listState.rows.length === 0 && (
          <p className="admin-users__status">{t('admin.noAssignments')}</p>
        )}

        {listState.kind === 'ok' && listState.rows.length > 0 && (
          <ul className="role-list">
            {listState.rows.map((row) => (
              <RoleRow
                key={row.kaimahi_oid}
                row={row}
                isSelf={
                  currentUser.kind === 'ok' &&
                  row.kaimahi_oid.toLowerCase() ===
                    currentUser.data.user.oid.toLowerCase()
                }
                disabled={
                  actionState.kind === 'working' &&
                  actionState.oid === row.kaimahi_oid
                }
                onChangeRole={(role) =>
                  void onAssign(
                    row.kaimahi_oid,
                    role,
                    row.assignment_notes,
                  )
                }
                onRevoke={() => void onRevoke(row.kaimahi_oid)}
                onResetPin={() => void onResetPin(row.kaimahi_oid)}
              />
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Assign form — paste OID + pick role
// ---------------------------------------------------------------------------
type AssignFormProps = {
  currentUserOid: string;
  disabled: boolean;
  onSubmit: (
    oid: string,
    role: RoleId,
    notes: string | null,
    displayName: string | null,
  ) => Promise<void>;
};

function AssignForm({ currentUserOid, disabled, onSubmit }: AssignFormProps) {
  const { t } = useTranslation();
  const callApi = useApi();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResponse['rows']>([]);
  const [searchState, setSearchState] = useState<
    | { kind: 'idle' }
    | { kind: 'searching' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [showDropdown, setShowDropdown] = useState(false);

  const [selected, setSelected] = useState<{
    oid: string;
    display_name: string;
    mail: string;
  } | null>(null);

  const [role, setRole] = useState<RoleId>('manager');

  // Debounced search: fires 300ms after the user stops typing, only if
  // 2+ characters and no user currently selected. Cancels stale searches
  // via the cancelled flag so race conditions don't repopulate the list.
  useEffect(() => {
    if (selected) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearchState({ kind: 'idle' });
      setShowDropdown(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setSearchState({ kind: 'searching' });
      setShowDropdown(true);
      void (async () => {
        try {
          const data = await callApi<UserSearchResponse>(
            `/admin/users/search?q=${encodeURIComponent(trimmed)}`,
          );
          if (cancelled) return;
          setResults(data.rows);
          setSearchState({ kind: 'idle' });
        } catch (err) {
          if (cancelled) return;
          const message =
            err instanceof ApiError
              ? `${String(err.status)} — ${err.message}`
              : err instanceof Error
                ? err.message
                : 'Search failed';
          setSearchState({ kind: 'error', message });
          setResults([]);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // callApi intentionally excluded: useApi() returns a fresh closure
    // every render, which would re-fire this effect after each setState
    // and trigger a render→effect→fetch→setState loop. Closure capture
    // of callApi at effect-creation time is fine for a debounced search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selected]);

  const handleSelect = (row: UserSearchResponse['rows'][number]) => {
    setSelected(row);
    setQuery(row.display_name);
    setShowDropdown(false);
    setResults([]);
  };

  const handleClearSelection = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    await onSubmit(selected.oid, role, null, selected.display_name);
    setSelected(null);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const isSelf =
    selected !== null &&
    selected.oid.toLowerCase() === currentUserOid.toLowerCase() &&
    currentUserOid !== '';

  return (
    <section className="admin-users__section">
      <h3 className="admin-users__section-title">{t('admin.assignTitle')}</h3>
      <form
        className="assign-form assign-form--compact"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <div className="assign-form__field assign-form__field--grow">
          <label htmlFor="assign-search">{t('admin.lookup.searchLabel')}</label>
          {selected ? (
            <div className="assign-form__selected">
              <div className="assign-form__selected-text">
                <strong>{selected.display_name}</strong>
                <span className="assign-form__selected-mail">{selected.mail}</span>
              </div>
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={handleClearSelection}
                disabled={disabled}
              >
                {t('admin.lookup.clear')}
              </button>
            </div>
          ) : (
            <div className="assign-form__search">
              <input
                id="assign-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setShowDropdown(true)}
                placeholder={t('admin.lookup.searchPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                disabled={disabled}
                aria-expanded={showDropdown}
                aria-autocomplete="list"
                role="combobox"
              />
              {showDropdown && (
                <ul className="assign-form__dropdown" role="listbox">
                  {searchState.kind === 'searching' && (
                    <li className="assign-form__dropdown-status">
                      {t('admin.lookup.searching')}
                    </li>
                  )}
                  {searchState.kind === 'error' && (
                    <li className="assign-form__dropdown-status assign-form__dropdown-status--error">
                      {searchState.message}
                    </li>
                  )}
                  {searchState.kind === 'idle' && results.length === 0 && (
                    <li className="assign-form__dropdown-status">
                      {t('admin.lookup.noResults')}
                    </li>
                  )}
                  {results.map((row) => (
                    <li key={row.oid}>
                      <button
                        type="button"
                        className="assign-form__dropdown-item"
                        onClick={() => handleSelect(row)}
                      >
                        <span className="assign-form__dropdown-name">
                          {row.display_name}
                        </span>
                        <span className="assign-form__dropdown-mail">
                          {row.mail}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="assign-form__field">
          <label htmlFor="assign-role">{t('admin.roleLabel')}</label>
          <select
            id="assign-role"
            value={role}
            onChange={(e) => setRole(e.target.value as RoleId)}
            disabled={disabled}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.role.${r}`)}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="auth-button auth-button--primary assign-form__submit"
          disabled={disabled || !selected}
        >
          {t('admin.assignButton')}
        </button>

        {isSelf && (
          <span className="assign-form__validation assign-form__validation--row">
            {t('admin.assigningSelfHint')}
          </span>
        )}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Role row — one user's assignment
// ---------------------------------------------------------------------------
type RoleRowProps = {
  row: RoleAssignment;
  isSelf: boolean;
  disabled: boolean;
  onChangeRole: (role: RoleId) => void;
  onRevoke: () => void;
  onResetPin: () => void;
};

function RoleRow({
  row,
  isSelf,
  disabled,
  onChangeRole,
  onRevoke,
  onResetPin,
}: RoleRowProps) {
  const { t } = useTranslation();

  return (
    <li className={`role-row role-row--${row.role_id}`}>
      <div className="role-row__main">
        <p className="role-row__name">
          {row.display_name ?? (
            <span className="role-row__name--unresolved">
              {t('admin.unresolvedUser')}
            </span>
          )}
          {isSelf && (
            <span className="role-row__self-badge">{t('admin.you')}</span>
          )}
        </p>
        <p className="role-row__meta">
          {row.assigned_at
            ? formatDateTime(row.assigned_at)
            : t('admin.defaultRoleHint')}
          {row.assignment_notes && (
            <>
              {' '}· <em>{row.assignment_notes}</em>
            </>
          )}
        </p>
      </div>

      <div className="role-row__actions">
        <label className="role-row__role-label">
          <span className="visually-hidden">{t('admin.roleLabel')}</span>
          <select
            value={row.role_id}
            onChange={(e) => onChangeRole(e.target.value as RoleId)}
            disabled={disabled || isSelf}
            aria-label={t('admin.changeRoleAria', { oid: row.kaimahi_oid })}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.role.${r}`)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="auth-button auth-button--secondary"
          onClick={onResetPin}
          disabled={disabled}
          aria-label={t('admin.resetPinAria', { oid: row.kaimahi_oid })}
        >
          {t('admin.resetPinButton')}
        </button>

        <button
          type="button"
          className="auth-button auth-button--secondary"
          onClick={onRevoke}
          disabled={disabled || isSelf || row.assigned_at === null}
          aria-label={t('admin.revokeAria', { oid: row.kaimahi_oid })}
          title={row.assigned_at === null ? t('admin.revokeDisabledHint') : undefined}
        >
          {t('admin.revokeButton')}
        </button>
      </div>
    </li>
  );
}

export default AdminUsers;
